import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Check, Lock, Mic2, Youtube, Info, LogOut, Music, Search, X, Heart } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Header } from "@/components/Header";
import { toast } from "sonner";
import { extractYouTubeId } from "@/lib/youtube";
import { getKFavs, isKFav, toggleKFav } from "@/lib/karaokeFavs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sk_listener_token";

const TrackCard = ({ videoId, title, channel, thumbnail, onPlay, fav, onFav, pinned, testid }) => (
  <div className="relative rounded-2xl overflow-hidden" style={{ background: "var(--card)", border: `2px solid ${pinned ? "var(--accent)" : "var(--border)"}` }}>
    <button onClick={onPlay} data-testid={testid} className="block w-full text-left transition-transform active:scale-95 hover:scale-[1.02]">
      <img src={thumbnail} alt="" className="w-full aspect-video object-cover" loading="lazy" />
      <div className="p-2.5">
        <p className="font-ui text-xs font-bold line-clamp-2" style={{ color: "var(--text)" }}>{title}</p>
        <p className="font-ui text-[11px] mt-1 truncate" style={{ color: "var(--text-muted)" }}>{channel}</p>
      </div>
    </button>
    <button onClick={onFav} data-testid={`karaoke-fav-${videoId}`} aria-label="Save to favourites"
      className="absolute top-2 right-2 grid place-items-center w-9 h-9 rounded-full transition-transform active:scale-90"
      style={{ background: "rgba(0,0,0,0.55)", color: fav ? "#f87171" : "#fff" }}>
      <Heart size={16} fill={fav ? "currentColor" : "none"} />
    </button>
  </div>
);

const LB = {
  title: { si: "කැරෝකේ කාමරේ", en: "Karaoke Room", ta: "கரோக்கே அறை" },
  members_only: { si: "සාමාජිකයන් සඳහා පමණයි · පෞද්ගලිකයි", en: "Members only · Private", ta: "உறுப்பினர்களுக்கு மட்டும் · தனிப்பட்டது" },
  gate_head: { si: "ඔබේ දුරකථනය තහවුරු කරන්න", en: "Verify your phone to enter", ta: "நுழைய உங்கள் தொலைபேசியை சரிபார்க்கவும்" },
  gate_sub: { si: "කැරෝකේ කාමරේ සාමාජිකයන්ට පමණි. පිවිසීමට ඔබේ දුරකථන අංකය තහවුරු කරන්න.", en: "The Karaoke Room is for members. Verify your phone number to enter.", ta: "கரோக்கே அறை உறுப்பினர்களுக்கானது. நுழைய உங்கள் எண்ணை சரிபார்க்கவும்." },
  phone_ph: { si: "දුරකථන අංකය (07XXXXXXXX)", en: "Phone number (07XXXXXXXX)", ta: "தொலைபேசி எண் (07XXXXXXXX)" },
  send_code: { si: "කේතය එවන්න", en: "Send code", ta: "குறியீட்டை அனுப்பு" },
  resend: { si: "නැවත එවන්න", en: "Resend", ta: "மீண்டும் அனுப்பு" },
  code_ph: { si: "6-ඉලක්කම් කේතය", en: "6-digit code", ta: "6-இலக்க குறியீடு" },
  verify: { si: "තහවුරු කරන්න", en: "Verify", ta: "சரிபார்" },
  verified: { si: "තහවුරුයි", en: "Verified", ta: "சரிபார்க்கப்பட்டது" },
  demo_note: { si: "ආදර්ශන ආකාරය — ඔබේ කේතය", en: "Demo mode — your code is", ta: "டெமோ முறை — உங்கள் குறியீடு" },
  demo_tail: { si: "(අප දියත් කරන විට සැබෑ SMS එකක් යැවේ).", en: "(a real SMS is sent when we launch).", ta: "(நாங்கள் தொடங்கும்போது உண்மையான SMS அனுப்பப்படும்)." },
  paste_label: { si: "නැත්නම් YouTube සබැඳියක් අලවන්න", en: "Or paste a YouTube link", ta: "அல்லது YouTube இணைப்பை ஒட்டவும்" },
  search_label: { si: "කැරෝකේ සින්දුවක් හොයන්න", en: "Find a karaoke track", ta: "கரோக்கே பாடலைத் தேடுங்கள்" },
  picks_label: { si: "තෝරාගත් කැරෝකේ — එක් ස්පර්ශයකින්", en: "Handpicked karaoke — one tap", ta: "தேர்ந்தெடுத்த கரோக்கே — ஒரு தட்டல்" },
  favs_label: { si: "ඔබ සුරැකූ කැරෝකේ", en: "Your saved karaoke", ta: "நீங்கள் சேமித்த கரோக்கே" },
  cat_devotional: { si: "භක්ති", en: "Devotional", ta: "பக்தி" },
  cat_film: { si: "පැරණි චිත්‍රපට", en: "Old films", ta: "பழைய திரைப்படம்" },
  cat_folk: { si: "ජන ගී", en: "Folk", ta: "நாட்டுப்புறம்" },
  cat_general: { si: "තවත්", en: "More", ta: "மேலும்" },
  cat_all: { si: "සියල්ල", en: "All", ta: "அனைத்தும்" },
  search_ph: { si: "උදා: සුනිල් සාන්ත, Olu Pipila…", en: "e.g. Sunil Santha, Olu Pipila…", ta: "எ.கா: சுனில் சாந்த…" },
  search_btn: { si: "හොයන්න", en: "Search", ta: "தேடு" },
  searching: { si: "හොයමින්…", en: "Searching…", ta: "தேடுகிறது…" },
  no_results: { si: "සින්දු හමු නොවීය. වෙනත් නමකින් උත්සාහ කරන්න.", en: "No tracks found. Try another name.", ta: "பாடல்கள் இல்லை. வேறு பெயரில் முயற்சிக்கவும்." },
  paste_ph: { si: "https://www.youtube.com/watch?v=...", en: "https://www.youtube.com/watch?v=...", ta: "https://www.youtube.com/watch?v=..." },
  load: { si: "සින්දුව අහන්න", en: "Load track", ta: "பாடலை ஏற்று" },
  change: { si: "වෙනත් සින්දුවක්", en: "Change track", ta: "வேறு பாடல்" },
  invalid: { si: "වලංගු YouTube සබැඳියක් නොවේ.", en: "That is not a valid YouTube link.", ta: "இது சரியான YouTube இணைப்பு அல்ல." },
  empty_head: { si: "ගයන්න සූදානම්ද?", en: "Ready to sing?", ta: "பாட தயாரா?" },
  empty_sub: { si: "ඔබ කැමති කැරෝකේ සින්දුවක YouTube සබැඳිය ඉහත අලවා 'සින්දුව අහන්න' ඔබන්න.", en: "Paste the YouTube link of any karaoke track above and tap 'Load track'.", ta: "மேலே எந்த கரோக்கே பாடலின் YouTube இணைப்பையும் ஒட்டி 'பாடலை ஏற்று' தட்டவும்." },
  note_private: { si: "පෞද්ගලික ගායනය පමණි — කිසිවක් පටිගත නොවේ, සුරැකෙන්නේ නැත, පළ නොවේ.", en: "Private sing-along only — nothing is recorded, saved or published.", ta: "தனிப்பட்ட பாடல் மட்டும் — எதுவும் பதிவு செய்யப்படவில்லை, சேமிக்கப்படவில்லை, வெளியிடப்படவில்லை." },
  note_license: { si: "සින්දු YouTube හි නිල වාදකයෙන් වාදනය වේ. ඒවා අපගේ නොවේ.", en: "Tracks play from YouTube's official player. They are not owned by us.", ta: "பாடல்கள் YouTube-இன் அதிகாரப்பூர்வ பிளேயரில் இயங்கும். அவை எங்களுடையவை அல்ல." },
  signout: { si: "කැරෝකේ වෙතින් ඉවත් වන්න", en: "Leave karaoke", ta: "கரோக்கேவிலிருந்து வெளியேறு" },
  back: { si: "ආපසු", en: "Back", ta: "பின்" },
};

export default function Karaoke() {
  const { lang, fontClass } = useLang();
  const L = (k) => (LB[k] && (LB[k][lang] || LB[k].en)) || k;

  const [checking, setChecking] = useState(true);
  const [token, setToken] = useState("");

  const [phone, setPhone] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [demoCode, setDemoCode] = useState("");

  const [urlInput, setUrlInput] = useState("");
  const [videoId, setVideoId] = useState("");
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [picks, setPicks] = useState([]);
  const [kfavs, setKfavs] = useState(getKFavs());
  const [catFilter, setCatFilter] = useState("all");

  useEffect(() => {
    const sync = () => setKfavs(getKFavs());
    window.addEventListener("sk-kfavs-changed", sync);
    return () => window.removeEventListener("sk-kfavs-changed", sync);
  }, []);
  const onFav = (item) => toggleKFav(item);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/karaoke/picks`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setPicks(r.data || [])).catch(() => {});
  }, [token]);

  const phoneValid = phone.trim().replace(/\D/g, "").length >= 9;

  useEffect(() => {
    const saved = localStorage.getItem(TOKEN_KEY);
    if (!saved) { setChecking(false); return; }
    axios.get(`${API}/listener/me`, { headers: { Authorization: `Bearer ${saved}` } })
      .then(() => setToken(saved))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const sendOtp = async () => {
    setOtpBusy(true);
    try {
      const { data } = await axios.post(`${API}/listener/request-otp`, { phone });
      setOtpSent(true);
      setDemoCode(data.demo ? data.demo_code : "");
      toast.success(data.demo ? "Demo mode: your code is shown below." : "We sent a code to your phone.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the code");
    } finally { setOtpBusy(false); }
  };

  const verifyOtp = async () => {
    setOtpBusy(true);
    try {
      const { data } = await axios.post(`${API}/listener/verify-otp`, { phone, code: otpCode });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Verified 🎉");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally { setOtpBusy(false); }
  };

  const load = () => {
    const id = extractYouTubeId(urlInput);
    if (!id) { toast.error(L("invalid")); return; }
    setVideoId(id);
  };

  const playTrack = (track) => {
    setVideoId(track.video_id);
    axios.post(`${API}/karaoke/play`, track, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  const runSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/karaoke/search`, { params: { q: query }, headers: { Authorization: `Bearer ${token}` } });
      setResults(data.results || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed. Please try again.");
    } finally { setSearching(false); }
  };

  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setVideoId("");
    setUrlInput("");
    setOtpSent(false);
    setOtpCode("");
  };

  return (
    <div className="min-h-screen pb-28" style={{ background: "var(--bg)" }}>
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/rooms/sindu-kamare" data-testid="karaoke-back" className="inline-flex items-center gap-2 font-ui text-sm font-bold mb-6" style={{ color: "var(--text-2)" }}>
          <ArrowLeft size={16} /> {L("back")}
        </Link>

        <div className="text-center mb-8">
          <span className="grid place-items-center w-16 h-16 rounded-full mx-auto" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><Mic2 size={30} /></span>
          <h1 className={`${fontClass()} text-3xl font-bold mt-3`} style={{ color: "var(--text)" }}>{L("title")}</h1>
          <p className="font-ui text-sm mt-1 inline-flex items-center gap-1.5" style={{ color: "var(--text-muted)" }}><Lock size={13} /> {L("members_only")}</p>
        </div>

        {checking && <div className="flex justify-center py-16"><Loader2 className="animate-spin" style={{ color: "var(--accent)" }} /></div>}

        {/* ---------- GATE ---------- */}
        {!checking && !token && (
          <div className="rounded-3xl p-6 sm:p-8" style={{ background: "var(--card)", border: "2px solid var(--border)" }} data-testid="karaoke-gate">
            <h2 className={`${fontClass()} text-xl font-bold text-center`} style={{ color: "var(--text)" }}>{L("gate_head")}</h2>
            <p className="font-ui text-sm text-center mt-2 mb-6" style={{ color: "var(--text-muted)" }}>{L("gate_sub")}</p>

            <div className="flex gap-2">
              <input data-testid="karaoke-phone" value={phone} onChange={(e) => { setPhone(e.target.value); setOtpSent(false); }} placeholder={L("phone_ph")} inputMode="tel"
                className="flex-1 h-14 px-5 rounded-2xl font-ui outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
              <button onClick={sendOtp} disabled={!phoneValid || otpBusy} data-testid="karaoke-send-otp"
                className="h-14 px-4 rounded-2xl font-ui font-bold text-sm whitespace-nowrap disabled:opacity-50" style={{ background: "var(--brass-light)", color: "var(--accent)", border: "2px solid var(--accent)" }}>
                {otpBusy ? <Loader2 size={18} className="animate-spin" /> : otpSent ? L("resend") : L("send_code")}
              </button>
            </div>

            {otpSent && (
              <div className="mt-4" data-testid="karaoke-otp-box">
                {demoCode && (
                  <p className="font-ui text-xs mb-3 px-3 py-2 rounded-xl" style={{ background: "var(--brass-light)", color: "var(--accent)" }} data-testid="karaoke-demo-code">
                    {L("demo_note")} <b style={{ letterSpacing: "3px" }}>{demoCode}</b> {L("demo_tail")}
                  </p>
                )}
                <div className="flex gap-2">
                  <input data-testid="karaoke-otp-input" value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" inputMode="numeric"
                    className="flex-1 h-14 px-5 rounded-2xl font-ui outline-none tracking-[6px] text-center text-xl" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
                  <button onClick={verifyOtp} disabled={otpCode.length < 6 || otpBusy} data-testid="karaoke-verify-otp"
                    className="h-14 px-5 rounded-2xl font-ui font-bold disabled:opacity-50 inline-flex items-center gap-2" style={{ background: "var(--accent)", color: "#fff" }}>
                    {otpBusy ? <Loader2 size={18} className="animate-spin" /> : <><Check size={18} /> {L("verify")}</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ---------- KARAOKE ---------- */}
        {!checking && token && (
          <div data-testid="karaoke-room">
            <div className="rounded-2xl p-4 mb-5 flex items-start gap-2" style={{ background: "var(--card)", border: "1px solid var(--border)" }}>
              <Info size={16} className="mt-0.5 shrink-0" style={{ color: "var(--accent)" }} />
              <div className="font-ui text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>
                <p>{L("note_private")}</p>
                <p className="mt-1">{L("note_license")}</p>
              </div>
            </div>

            {videoId ? (
              <div data-testid="karaoke-player">
                <div className="relative w-full rounded-3xl overflow-hidden" style={{ paddingTop: "56.25%", background: "#000", border: "2px solid var(--border)" }}>
                  <iframe
                    key={videoId}
                    data-testid="karaoke-iframe"
                    className="absolute inset-0 w-full h-full"
                    src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`}
                    title="Karaoke"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <button onClick={() => setVideoId("")} data-testid="karaoke-change"
                  className="mt-4 inline-flex items-center gap-2 h-12 px-5 rounded-full font-ui font-bold text-sm" style={{ background: "var(--card)", color: "var(--text)", border: "2px solid var(--border)" }}>
                  <Music size={16} style={{ color: "var(--accent)" }} /> {L("change")}
                </button>
              </div>
            ) : (
              <>
                {kfavs.length > 0 && (
                  <div className="mb-8" data-testid="karaoke-favs">
                    <label className="font-ui text-sm font-bold block mb-2" style={{ color: "var(--text-2)" }}>❤ {L("favs_label")}</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {kfavs.map((r) => (
                        <TrackCard key={r.video_id} videoId={r.video_id} title={r.title} channel={r.channel} thumbnail={r.thumbnail}
                          onPlay={() => playTrack(r)} fav={true} onFav={() => onFav(r)} testid={`karaoke-favplay-${r.video_id}`} />
                      ))}
                    </div>
                  </div>
                )}

                {picks.length > 0 && (
                  <div className="mb-8" data-testid="karaoke-picks">
                    <label className="font-ui text-sm font-bold block mb-2" style={{ color: "var(--text-2)" }}>🪔 {L("picks_label")}</label>
                    {(() => {
                      const avail = ["devotional", "film", "folk", "general"].filter((c) => picks.some((p) => (p.category || "general") === c));
                      if (avail.length < 2) return null;
                      return (
                        <div className="flex flex-wrap gap-2 mb-3" data-testid="karaoke-cat-filters">
                          {["all", ...avail].map((c) => (
                            <button key={c} onClick={() => setCatFilter(c)} data-testid={`karaoke-filter-${c}`}
                              className="h-9 px-4 rounded-full font-ui font-bold text-xs transition-transform active:scale-95"
                              style={{ background: catFilter === c ? "var(--accent)" : "var(--card)", color: catFilter === c ? "#fff" : "var(--text-2)", border: "1px solid var(--border)" }}>
                              {c === "all" ? L("cat_all") : L(`cat_${c}`)}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                    {["devotional", "film", "folk", "general"].map((cat) => {
                      if (catFilter !== "all" && catFilter !== cat) return null;
                      const items = picks.filter((p) => (p.category || "general") === cat);
                      if (!items.length) return null;
                      const mixed = catFilter === "all" && picks.some((p) => (p.category || "general") !== "general");
                      return (
                        <div key={cat} className="mt-3" data-testid={`karaoke-cat-${cat}`}>
                          {mixed && <p className="font-ui text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: "var(--text-muted)" }}>{L(`cat_${cat}`)}</p>}
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {items.map((r) => (
                              <TrackCard key={r.id} videoId={r.video_id} title={r.title} channel={r.channel} thumbnail={r.thumbnail} pinned
                                onPlay={() => playTrack({ video_id: r.video_id, title: r.title, channel: r.channel, thumbnail: r.thumbnail })} fav={isKFav(r.video_id)} onFav={() => onFav(r)} testid={`karaoke-pick-${r.video_id}`} />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <label className="font-ui text-sm font-bold block mb-2" style={{ color: "var(--text-2)" }}>{L("search_label")}</label>
                <div className="flex gap-2 mb-4">
                  <div className="flex-1 relative">
                    <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
                    <input data-testid="karaoke-search-input" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} placeholder={L("search_ph")}
                      className="w-full h-14 pl-11 pr-10 rounded-2xl font-ui outline-none" style={{ background: "var(--card)", border: "2px solid var(--border)", color: "var(--text)" }} />
                    {query && <button onClick={() => { setQuery(""); setResults(null); }} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }}><X size={18} /></button>}
                  </div>
                  <button onClick={runSearch} disabled={!query.trim() || searching} data-testid="karaoke-search-btn"
                    className="h-14 px-5 rounded-2xl font-ui font-bold disabled:opacity-50 inline-flex items-center gap-2" style={{ background: "var(--accent)", color: "#fff" }}>
                    {searching ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />} <span className="hidden sm:inline">{L("search_btn")}</span>
                  </button>
                </div>

                {results !== null && results.length === 0 && (
                  <p className="font-ui text-sm mb-6" style={{ color: "var(--text-muted)" }} data-testid="karaoke-no-results">{L("no_results")}</p>
                )}
                {results !== null && results.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8" data-testid="karaoke-results">
                    {results.map((r) => (
                      <TrackCard key={r.videoId} videoId={r.videoId} title={r.title} channel={r.channel} thumbnail={r.thumbnail}
                        onPlay={() => playTrack({ video_id: r.videoId, title: r.title, channel: r.channel, thumbnail: r.thumbnail })} fav={isKFav(r.videoId)} onFav={() => onFav({ video_id: r.videoId, title: r.title, channel: r.channel, thumbnail: r.thumbnail })} testid={`karaoke-result-${r.videoId}`} />
                    ))}
                  </div>
                )}

                {results === null && picks.length === 0 && kfavs.length === 0 && (
                  <div className="rounded-3xl p-10 text-center mb-6" style={{ background: "var(--card)", border: "2px dashed var(--border)" }} data-testid="karaoke-empty">
                    <span className="grid place-items-center w-14 h-14 rounded-full mx-auto" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><Youtube size={26} /></span>
                    <p className={`${fontClass()} text-lg font-bold mt-3`} style={{ color: "var(--text)" }}>{L("empty_head")}</p>
                    <p className="font-ui text-sm mt-1" style={{ color: "var(--text-muted)" }}>{L("empty_sub")}</p>
                  </div>
                )}

                <label className="font-ui text-sm font-bold block mb-2" style={{ color: "var(--text-2)" }}>{L("paste_label")}</label>
                <div className="flex gap-2">
                  <input data-testid="karaoke-url-input" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder={L("paste_ph")}
                    className="flex-1 h-14 px-5 rounded-2xl font-ui outline-none" style={{ background: "var(--card)", border: "2px solid var(--border)", color: "var(--text)" }} />
                  <button onClick={load} disabled={!urlInput.trim()} data-testid="karaoke-load"
                    className="h-14 px-5 rounded-2xl font-ui font-bold disabled:opacity-50 inline-flex items-center gap-2" style={{ background: "var(--card)", color: "var(--accent)", border: "2px solid var(--accent)" }}>
                    <Youtube size={18} /> {L("load")}
                  </button>
                </div>
              </>
            )}

            <div className="text-center mt-10">
              <button onClick={signOut} data-testid="karaoke-signout" className="inline-flex items-center gap-2 font-ui text-xs font-bold" style={{ color: "var(--text-muted)" }}>
                <LogOut size={14} /> {L("signout")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
